import { c as _c } from "react/compiler-runtime";
import type { SelectOptionType, UrlSchemeType } from '@systemeio/tip-tap';
import { UrlSchemeEnum } from '@systemeio/types';
import type { TFunctionType } from '@systemeio/util';
import { forwardRef, useEffect, useMemo, useState, type ChangeEvent, type ReactNode } from 'react';
import { CopyInputValueButton } from './copy-input-value-button';
import { FormInputGroup, type FormInputGroupProps } from './form-input';
import type { FormSelectDataType } from './form-select/form-select';
import { InputWithSelectsTrigger } from './input-with-selects';
import { Select } from './select';
type UrlScheme = UrlSchemeEnum;
type UrlSchemeOption = FormSelectDataType<UrlSchemeEnum>;
interface FormInputUrlProps extends Omit<FormInputGroupProps, 'onChange' | 'prefix' | 'startControl' | 'suffix' | 'type' | 'value'> {
  onChange: (value: string) => void;
  prefix?: ReactNode;
  schemeOptions?: UrlSchemeOption[];
  value?: string;
  withCopyButton?: boolean;
}
interface UrlSchemeSelectProps {
  activeScheme: UrlScheme;
  disabled?: boolean;
  isPreFetching?: boolean;
  options: UrlSchemeOption[];
  size?: FormInputGroupProps['size'];
  onChange: (scheme: UrlScheme) => void;
}
const HTTPS_SCHEME = UrlSchemeEnum.Https;
const getUrlSchemeOptions = (t: TFunctionType): UrlSchemeOption[] => [{
  caption: HTTPS_SCHEME,
  customCaption: t('input.url.site'),
  id: HTTPS_SCHEME
}, {
  caption: UrlSchemeEnum.Mailto,
  customCaption: t('input.url.mail'),
  id: UrlSchemeEnum.Mailto
}, {
  caption: UrlSchemeEnum.Tel,
  customCaption: t('input.url.tel'),
  id: UrlSchemeEnum.Tel
}];

/**
 * The same three schemes in the option shape the rich-text editor's `extensionOptions` expects.
 * Kept next to `getUrlSchemeOptions` so both builders share one source of the localized labels.
 */
const getEditorUrlSchemeOptions = (t: TFunctionType): SelectOptionType<UrlSchemeType>[] => getUrlSchemeOptions(t).map(option => ({
  value: option.id,
  label: option.caption,
  customLabel: option.customCaption
}));
const getSchemeFromValue = (value: string, options: UrlSchemeOption[]) => options.find(option => value.startsWith(option.id))?.id;
const getDefaultScheme = (options: UrlSchemeOption[]) => options[0]?.id ?? HTTPS_SCHEME;
const stripScheme = (value: string, options: UrlSchemeOption[]) => {
  const scheme = getSchemeFromValue(value, options);
  return scheme ? value.slice(scheme.length) : value;
};
const placeholders: Record<UrlScheme, string> = {
  [UrlSchemeEnum.Https]: 'example.com',
  [UrlSchemeEnum.Mailto]: 'contact@example.com',
  [UrlSchemeEnum.Tel]: '+15551234567'
};
const UrlSchemeSelect = t0 => {
  const $ = _c(16);
  const {
    activeScheme,
    disabled,
    isPreFetching,
    options,
    size,
    onChange
  } = t0;
  let t1;
  if ($[0] !== activeScheme || $[1] !== options) {
    t1 = options.find(option => option.id === activeScheme)?.caption;
    $[0] = activeScheme;
    $[1] = options;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const activeCaption = t1;
  let t2;
  if ($[3] !== onChange) {
    t2 = scheme => scheme && onChange(scheme);
    $[3] = onChange;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = size ?? undefined;
  const t4 = size ?? "md";
  let t5;
  if ($[5] !== activeCaption || $[6] !== t4) {
    t5 = <InputWithSelectsTrigger side="start" size={t4}>{activeCaption}</InputWithSelectsTrigger>;
    $[5] = activeCaption;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== activeScheme || $[9] !== disabled || $[10] !== isPreFetching || $[11] !== options || $[12] !== t2 || $[13] !== t3 || $[14] !== t5) {
    t6 = <Select data={options} disabled={disabled} isPreFetching={isPreFetching} onChange={t2} placeholder="URL scheme" size={t3} trigger={t5} value={activeScheme} />;
    $[8] = activeScheme;
    $[9] = disabled;
    $[10] = isPreFetching;
    $[11] = options;
    $[12] = t2;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
};
const FormInputUrl = forwardRef<HTMLInputElement, FormInputUrlProps>((t0, ref) => {
  const $ = _c(48);
  let inputProps;
  let onChange;
  let placeholder;
  let prefix;
  let schemeOptions;
  let t1;
  let withCopyButton;
  if ($[0] !== t0) {
    ({
      onChange,
      placeholder,
      prefix,
      schemeOptions,
      value: t1,
      withCopyButton,
      ...inputProps
    } = t0);
    $[0] = t0;
    $[1] = inputProps;
    $[2] = onChange;
    $[3] = placeholder;
    $[4] = prefix;
    $[5] = schemeOptions;
    $[6] = t1;
    $[7] = withCopyButton;
  } else {
    inputProps = $[1];
    onChange = $[2];
    placeholder = $[3];
    prefix = $[4];
    schemeOptions = $[5];
    t1 = $[6];
    withCopyButton = $[7];
  }
  const value = t1 === undefined ? "" : t1;
  let t2;
  if ($[8] !== schemeOptions) {
    t2 = schemeOptions ?? [{
      caption: HTTPS_SCHEME,
      id: HTTPS_SCHEME
    }];
    $[8] = schemeOptions;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const options = t2;
  let t3;
  if ($[10] !== options || $[11] !== value) {
    t3 = getSchemeFromValue(value, options) ?? getDefaultScheme(options);
    $[10] = options;
    $[11] = value;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const [activeScheme, setActiveScheme] = useState(t3);
  let t4;
  let t5;
  if ($[13] !== activeScheme || $[14] !== options || $[15] !== value) {
    t4 = () => {
      const nextScheme = getSchemeFromValue(value, options);
      if (nextScheme && nextScheme !== activeScheme) {
        setActiveScheme(nextScheme);
      }
    };
    t5 = [activeScheme, options, value];
    $[13] = activeScheme;
    $[14] = options;
    $[15] = value;
    $[16] = t4;
    $[17] = t5;
  } else {
    t4 = $[16];
    t5 = $[17];
  }
  useEffect(t4, t5);
  let t6;
  if ($[18] !== activeScheme || $[19] !== onChange || $[20] !== options) {
    t6 = event => {
      const nextValue = event.target.value;
      if (!nextValue) {
        return onChange("");
      }
      const typedScheme = getSchemeFromValue(nextValue, options);
      if (typedScheme) {
        setActiveScheme(typedScheme);
        return onChange(typedScheme + stripScheme(nextValue, options));
      }
      onChange(activeScheme + stripScheme(nextValue, options));
    };
    $[18] = activeScheme;
    $[19] = onChange;
    $[20] = options;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const handleChange = t6;
  let t7;
  if ($[22] !== onChange || $[23] !== options || $[24] !== value) {
    t7 = nextScheme_0 => {
      setActiveScheme(nextScheme_0);
      const sanitizedValue = stripScheme(value, options);
      onChange(sanitizedValue ? nextScheme_0 + sanitizedValue : "");
    };
    $[22] = onChange;
    $[23] = options;
    $[24] = value;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const handleSchemeChange = t7;
  let t8;
  if ($[26] !== activeScheme || $[27] !== handleSchemeChange || $[28] !== inputProps || $[29] !== options || $[30] !== prefix) {
    t8 = prefix === undefined && options.length > 1 ? <UrlSchemeSelect activeScheme={activeScheme} disabled={inputProps.disabled} isPreFetching={inputProps.isPreFetching} options={options} size={inputProps.size} onChange={handleSchemeChange} /> : undefined;
    $[26] = activeScheme;
    $[27] = handleSchemeChange;
    $[28] = inputProps;
    $[29] = options;
    $[30] = prefix;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  const schemeSelect = t8;
  const t9 = placeholder ?? (schemeOptions ? placeholders[activeScheme] : undefined);
  const t10 = schemeSelect ? undefined : prefix ?? options[0]?.caption;
  let t11;
  if ($[32] !== inputProps || $[33] !== value || $[34] !== withCopyButton) {
    t11 = withCopyButton ? <CopyInputValueButton disabled={inputProps.disabled || inputProps.isPreFetching} text={value} /> : undefined;
    $[32] = inputProps;
    $[33] = value;
    $[34] = withCopyButton;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== options || $[37] !== value) {
    t12 = stripScheme(value, options);
    $[36] = options;
    $[37] = value;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== handleChange || $[40] !== inputProps || $[41] !== ref || $[42] !== schemeSelect || $[43] !== t10 || $[44] !== t11 || $[45] !== t12 || $[46] !== t9) {
    t13 = <FormInputGroup {...inputProps} ref={ref} inputMode="url" onChange={handleChange} placeholder={t9} prefix={t10} startControl={schemeSelect} trailingAction={t11} type="text" value={t12} />;
    $[39] = handleChange;
    $[40] = inputProps;
    $[41] = ref;
    $[42] = schemeSelect;
    $[43] = t10;
    $[44] = t11;
    $[45] = t12;
    $[46] = t9;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  return t13;
});
FormInputUrl.displayName = 'FormInputUrl';
export { FormInputUrl, getEditorUrlSchemeOptions, getUrlSchemeOptions };
export type { FormInputUrlProps, UrlSchemeOption };